import React, { Component } from 'react'
import '../styles/GlobalStyle.css'
import { firebase, db } from '../firebase/Firebase'
import { flattenDoc } from '.././Util'
import ColType from '.././Types'
import { Button } from 'antd'
import CustomFooter from '../login/CustomFooter'
import MathText, { shouldRenderMath } from './mathtext'

// Helper component: a div with fixed dimensions where the background image
// is rendered as an absolutely-positioned <img> scaled ~30% larger than the
// div, centered, and behind the content. The div clips overflow so the
// oversized image doesn't affect layout.
const ImageBox = ({ src, style, children, imgScale = 1.3, imgScaleWidth = 1.0,
    imageScaleHeight = 1.0, objectFit = 'contain', top = '50%', left = '50%', onClick, onTouchEnd }) => {
    let styleObj = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: `translate(-50%, -50%)`,
        width: `${imgScale * 100}%`,
        height: `${imgScale * 100}%`,
        objectFit: objectFit,
        pointerEvents: 'none',
        zIndex: 0,
    };
    if (top !== '50%') styleObj.top = top;
    if (left !== '50%') styleObj.left = left;
    if (imgScaleWidth !== 1.0) {
        styleObj.width = `${imgScaleWidth * 100}%`;
    }
    if (imageScaleHeight !== 1.0) {
        styleObj.height = `${imageScaleHeight * 100}%`;
    }
    return <div
        style={{
            position: 'relative',
            // overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'transparent',
            ...style,
        }}
        onClick={onClick}
        onTouchEnd={onTouchEnd}
    >
        <img
            src={`/games/castle/${src}`}
            alt=''
            draggable={false}
            style={styleObj}
        />
        <div style={{ position: 'relative', zIndex: 1, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            {children}
        </div>
    </div>
}
const shuffle = (arr) => {
    const a = [...arr]
    for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1))
        const t = a[i]; a[i] = a[j]; a[j] = t
    }
    return a
}
const PLAYER_COLORS = {
    player1: '#B8432E', // brick red (lightened)
    player2: '#3558B0', // deep blue (lightened)
}
const GOLD_DROP_SHADOW = 'drop-shadow(0 0 4px rgba(255, 200, 0, 1)) drop-shadow(0 0 10px rgba(255, 215, 0, 0.95)) drop-shadow(0 0 20px rgba(255, 215, 0, 0.7)) drop-shadow(0 0 35px rgba(255, 200, 0, 0.5))'
const GREEN_DROP_SHADOW = 'drop-shadow(0 0 3px rgba(46, 125, 50, 1)) drop-shadow(0 0 7px rgba(46, 125, 50, 0.95)) drop-shadow(0 0 15px rgba(46, 125, 50, 0.7)) drop-shadow(0 0 26px rgba(46, 125, 50, 0.5))'
const RED_DROP_SHADOW = 'drop-shadow(0 0 3px rgba(198, 40, 40, 1)) drop-shadow(0 0 7px rgba(198, 40, 40, 0.95)) drop-shadow(0 0 15px rgba(198, 40, 40, 0.7)) drop-shadow(0 0 26px rgba(198, 40, 40, 0.5))'
const dedupeAnswers = (answers) => {
    const norm = (t) => String(t == null ? '' : t).toLowerCase().trim().replace(/\s+/g, ' ').replace(/[.!?,;:]+$/, '')
    const seen = new Set()
    // Put the correct answer first so it wins any tie
    return [...answers]
        .sort((a, b) => (b.correct ? 1 : 0) - (a.correct ? 1 : 0))
        .filter(a => {
            const k = norm(a.answer)
            if (!k || seen.has(k)) return false
            seen.add(k)
            return true
        })
}
class CastleGame extends Component {
    state = {
        phase: 'intro',
        gameMode: null,
        currentTurn: 'player1',
        player1Score: 0,
        player2Score: 0,
        winScore: 5,
        currentQuestion: null,
        seenQuestionIndices: [],
        allQuestions: [],
        feedbackMessage: null,
        feedbackCorrect: null,
        showFeedback: false,
        selectedAnswerIdx: null,
        winner: null,
        game_data: this.props.gameQuestions,
        displayedAnswers: [],
    }

    renderMath = (text) => shouldRenderMath(this.state.game_data) ? <MathText text={text} /> : text

    componentDidMount() {
        const allQuestions = [
            ...this.state.game_data.problem_solving_questions.map((q, i) => ({
                ...q, sourceIndex: i, category: 'problem_solving'
            })),
            ...this.state.game_data.conceptual_questions.map((q, i) => ({
                ...q, sourceIndex: i, category: 'conceptual'
            })),
        ]
        this.setState({ allQuestions })
        this.startGame('multi');
    }
    componentDidUpdate(prevProps) {
        // If autoPlay flips on while a question is already showing, kick it off
        if (!prevProps.autoPlay && this.props.autoPlay &&
            this.state.phase === 'playing' && !this.state.showFeedback) {
            this.scheduleAutoAnswer()
        }
    }
    componentWillUnmount() {
        if (this.autoAnswerTimeout) clearTimeout(this.autoAnswerTimeout)
    }
    scheduleAutoAnswer = () => {
        if (!this.props.autoPlay) return
        if (this.autoAnswerTimeout) clearTimeout(this.autoAnswerTimeout)
        this.autoAnswerTimeout = setTimeout(() => {
            const { displayedAnswers, showFeedback, phase } = this.state
            if (phase !== 'playing' || !displayedAnswers || displayedAnswers.length === 0 || showFeedback) return
            const answers = displayedAnswers
            const correctIdx = answers.findIndex(a => a.correct)
            const wrongIndices = answers
                .map((_, i) => i)
                .filter(i => !answers[i].correct)
            let chosenIdx
            // 70% of the time pick the correct answer
            if (Math.random() < 0.7 && correctIdx !== -1) {
                chosenIdx = correctIdx
            } else if (wrongIndices.length > 0) {
                chosenIdx = wrongIndices[Math.floor(Math.random() * wrongIndices.length)]
            } else {
                chosenIdx = correctIdx
            }
            this.handleAnswerSelect(answers[chosenIdx], chosenIdx)
        }, 1000) // pause so the question is visible before it auto-answers
    }
    pickRandomQuestion = () => {
        const { allQuestions, seenQuestionIndices } = this.state
        let seen = [...seenQuestionIndices]
        if (seen.length >= allQuestions.length) seen = []
        const unseen = allQuestions.map((_, i) => i).filter(i => !seen.includes(i))
        const randomIdx = unseen[Math.floor(Math.random() * unseen.length)]
        seen.push(randomIdx)
        const question = allQuestions[randomIdx]
        this.setState({
            currentQuestion: question,
            displayedAnswers: shuffle(dedupeAnswers(question.answers)),
            seenQuestionIndices: seen,
            showFeedback: false,
            feedbackMessage: null,
            feedbackCorrect: null,
            selectedAnswerIdx: null,
        }, () => this.scheduleAutoAnswer())
    }
    startGame = (mode) => {
        this.setState({
            phase: 'playing',
            gameMode: mode,
            currentTurn: 'player1',
            player1Score: 0,
            player2Score: 0,
            seenQuestionIndices: [],
            winner: null,
        }, () => this.pickRandomQuestion())
    }
    handleAnswerSelect = (answer, idx) => {
        if (this.state.showFeedback) return
        const { gameMode, currentTurn, player1Score, player2Score, winScore } = this.state
        const isCorrect = answer.correct

        // report the answer up to Games.js for stats
        if (this.props.onAnswered) this.props.onAnswered(!!isCorrect);

        let newP1 = player1Score, newP2 = player2Score, nextTurn = currentTurn
        if (isCorrect) {
            if (currentTurn === 'player1') newP1++
            else newP2++
        }
        let winner = null
        if (newP1 >= winScore) winner = 'player1'
        else if (newP2 >= winScore) winner = 'player2'
        if (gameMode === 'multi') {
            nextTurn = currentTurn === 'player1' ? 'player2' : 'player1'
        }
        this.setState({
            player1Score: newP1,
            player2Score: newP2,
            showFeedback: true,
            feedbackCorrect: isCorrect,
            feedbackMessage: isCorrect ? 'Correct!' : 'Not quite!',
            selectedAnswerIdx: idx,
        })
        setTimeout(() => {
            if (winner) {
                setTimeout(() => {
                    this.setState({ phase: 'victory', winner }, () => {
                        if (this.props.autoPlay && this.props.autoPlayOnComplete) {
                            this.props.autoPlayOnComplete()
                        }
                    })
                }, 250)
            }
            else this.setState({ currentTurn: nextTurn }, () => this.pickRandomQuestion())
        }, 1500)
    }
    resetGame = () => {
        this.setState({
            phase: 'intro', gameMode: null, currentTurn: 'player1',
            player1Score: 0, player2Score: 0, currentQuestion: null,
            seenQuestionIndices: [], showFeedback: false,
            feedbackMessage: null, feedbackCorrect: null,
            selectedAnswerIdx: null, winner: null,
        })
    }
    renderLadderRungs = (score, side) => {
        const rungs = []
        for (let i = 0; i <= score; i++) {
            let styleObj = {
                position: 'absolute',
                bottom: `${21 + i * 8}vh`,
                width: '15vw',
                zIndex: 2,
            };
            if (side === 'left') {
                styleObj.left = '8vh';
            } else if (side === 'right') {
                styleObj.right = '8vh';
            }
            rungs.push(
                <img
                    key={`${side}-rung-${i}`}
                    src='/games/castle/castle-ladder.png'
                    alt='ladder rung'
                    style={styleObj}
                />
            )
        }
        return rungs
    }
    renderClimber = (score, side, isActive) => {
        const isLeft = side === 'left'
        const imgSrc = isLeft ? 'red-team-climbing.png' : 'blue-team-climbing.png'
        const baseBottom = 11 // starting position in vh (just below the first rung)
        const climbPerPoint = 8 // matches ladder rung spacing from renderLadderRungs
        const bottomPos = baseBottom + score * climbPerPoint
        const styleObj = {
            position: 'absolute',
            bottom: `${bottomPos}vh`,
            width: '38vh',
            zIndex: 3,
            transition: 'bottom 0.6s ease, filter 0.3s ease',
            pointerEvents: 'none',
            ...(isLeft
                ? { left: '2vh' }
                : { right: '2vh' }
            ),
            ...(isActive
                ? { filter: GOLD_DROP_SHADOW }
                : {}
            ),
        }
        return (
            <img
                key={`${side}-climber`}
                src={`/games/castle/${imgSrc}`}
                alt={`${side} climber`}
                draggable={false}
                style={styleObj}
            />
        )
    }
    renderIntro = () => {
        return (
            <div style={styles.overlay}>
                <ImageBox src='info-container.png' style={styles.introCard} imgScale={2.5}>
                    <h1 style={styles.title}>Castle Climb</h1>
                    <p style={styles.description}>
                        {this.state.game_data.description}
                    </p>
                </ImageBox>
                <div style={styles.modeButtons}>
                    <ImageBox
                        src='answer-container.png'
                        style={styles.modeButton}
                        imgScale={1.84}
                        onClick={() => this.startGame('single')}
                        onTouchEnd={(e) => { e.preventDefault(); this.startGame('single') }}
                    >
                        <div style={styles.modeIcon}>🏰</div>
                        <div style={styles.modeLabel}>Single Player</div>
                    </ImageBox>
                    <ImageBox
                        src='answer-container.png'
                        style={styles.modeButton}
                        imgScale={1.84}
                        onClick={() => this.startGame('multi')}
                        onTouchEnd={(e) => { e.preventDefault(); this.startGame('multi') }}
                    >
                        <div style={styles.modeIcon}>⚔️</div>
                        <div style={styles.modeLabel}>Multiplayer</div>
                    </ImageBox>
                </div>
            </div>
        )
    }
    renderScoreboard = () => {
        const { gameMode, player1Score, player2Score, winScore, currentTurn } = this.state
        const p1Active = currentTurn === 'player1'
        const p2Active = currentTurn === 'player2'
        return (
            <div style={styles.scoreboard}>
                <ImageBox src='scoreboard.png'
                    imgScale={2.0}
                    style={{
                        ...styles.scoreItem,
                        ...(p1Active ? { filter: GOLD_DROP_SHADOW } : {}),
                    }}>
                    <div style={{
                        ...styles.scoreLabel,
                        color: PLAYER_COLORS.player1,
                    }}>
                        {gameMode === 'single' ? 'You' : 'Player 1'}
                    </div>
                    <div style={styles.scoreValue}>{player1Score} / {winScore}</div>
                    <div style={styles.scoreBar}>
                        <div style={{ ...styles.scoreBarFill, width: `${(player1Score / winScore) * 100}%`, backgroundColor: PLAYER_COLORS.player1 }} />
                    </div>
                </ImageBox>
                {gameMode === 'multi' && (
                    <ImageBox src='scoreboard.png'
                        imgScale={2.0}
                        style={{
                            ...styles.scoreItem,
                            ...(p2Active ? { filter: GOLD_DROP_SHADOW } : {}),
                        }}>
                        <div style={{
                            ...styles.scoreLabel,
                            color: PLAYER_COLORS.player2,
                        }}>Player 2</div>
                        <div style={styles.scoreValue}>{player2Score} / {winScore}</div>
                        <div style={styles.scoreBar}>
                            <div style={{ ...styles.scoreBarFill, width: `${(player2Score / winScore) * 100}%`, backgroundColor: PLAYER_COLORS.player2 }} />
                        </div>
                    </ImageBox>
                )}
            </div>
        )
    }
    renderQuestion = () => {
        const { currentQuestion, displayedAnswers, showFeedback, feedbackMessage, feedbackCorrect, gameMode, currentTurn, selectedAnswerIdx } = this.state
        if (!currentQuestion) return null
        let turnLabel
        if (gameMode === 'single') {
            turnLabel = <span>Your Turn</span>
        } else if (currentTurn === 'player1') {
            turnLabel = <span><span style={{ color: PLAYER_COLORS.player1 }}>Player 1</span>'s Turn</span>
        } else {
            turnLabel = <span><span style={{ color: PLAYER_COLORS.player2 }}>Player 2</span>'s Turn</span>
        }
        return (
            <div style={styles.questionArea}>
                <ImageBox src='info-container.png' style={styles.turnBadge} imgScale={2.0}>
                    <span>{turnLabel}</span>
                </ImageBox>
                <ImageBox src='question-container.png' style={styles.questionCard} imgScale={2.5}>
                    <div style={styles.questionText}>
                        {this.renderMath(currentQuestion.question)}
                    </div>
                </ImageBox>
                <div style={styles.answersContainer}>
                    {displayedAnswers.map((ans, idx) => {
                        let extraStyle = {}
                        if (showFeedback) {
                            if (ans.correct) {
                                extraStyle = { ...styles.correctAnswer, filter: GREEN_DROP_SHADOW }
                            } else if (idx === selectedAnswerIdx && !ans.correct) {
                                extraStyle = { ...styles.incorrectAnswer, filter: RED_DROP_SHADOW }
                            } else {
                                extraStyle = styles.fadedAnswer
                            }
                        }
                        return (
                            <ImageBox
                                key={idx}
                                src='answer-container.png'
                                style={{ ...styles.answerCard, ...extraStyle }}
                                objectFit='initial'
                                imgScaleWidth={1.74}
                                imageScaleHeight={1.38}
                                top={'53%'}
                                onClick={() => this.handleAnswerSelect(ans, idx)}
                                onTouchEnd={(e) => { e.preventDefault(); this.handleAnswerSelect(ans, idx) }}
                            >
                                {/* Correct checkmark */}
                                {showFeedback && ans.correct && (
                                    <div style={styles.feedbackIcon}>✓</div>
                                )}
                                {/* Wrong X */}
                                {showFeedback && idx === selectedAnswerIdx && !ans.correct && (
                                    <div style={{ ...styles.feedbackIcon, ...styles.feedbackIconWrong }}>✗</div>
                                )}
                                <div style={styles.answerText}>
                                    {this.renderMath(ans.answer)}
                                </div>
                            </ImageBox>
                        )
                    })}
                </div>
                {/* Feedback banner using correct-incorrect-container.png */}
                {showFeedback && (
                    <ImageBox
                        src='correct-incorrect-container.png'
                        style={{
                            ...styles.feedbackBanner,
                            filter: feedbackCorrect ? GREEN_DROP_SHADOW : RED_DROP_SHADOW,
                        }}
                        imgScale={1.5}
                    >
                        <div style={{
                            ...styles.feedbackText,
                            color: feedbackCorrect ? '#1B5E20' : '#B71C1C',
                        }}>
                            {feedbackMessage}
                        </div>
                    </ImageBox>
                )}
            </div>
        )
    }
    renderPlaying = () => {
        const { player1Score, player2Score, gameMode, currentTurn } = this.state
        return (
            <div style={styles.overlay}>
                {this.renderLadderRungs(player1Score, 'left')}
                {gameMode === 'multi' && this.renderLadderRungs(player2Score, 'right')}
                {this.renderClimber(player1Score, 'left', currentTurn === 'player1')}
                {gameMode === 'multi' && this.renderClimber(player2Score, 'right', currentTurn === 'player2')}
                {this.renderScoreboard()}
                {this.renderQuestion()}
            </div>
        )
    }
    renderVictory = () => {
        const { winner, gameMode } = this.state
        let victoryText = gameMode === 'single'
            ? 'You Won!'
            : winner === 'player1' ? 'Player 1 Wins!' : 'Player 2 Wins!'
        const victoryImg = winner === 'player1' ? 'red-team-wins.png' : 'blue-team-wins.png'
        return (
            <div style={styles.overlay}>
                <ImageBox src={victoryImg} style={styles.victoryCharacter} imgScale={2}>
                </ImageBox>
                <ImageBox src='info-container.png' style={styles.victoryCard} imgScale={1.8}>
                    <div style={styles.victoryEmoji}>🏆</div>
                    <h1 style={{
                        ...styles.victoryTitle,
                        color: PLAYER_COLORS[winner],
                    }}>{victoryText}</h1>
                    <p style={styles.victorySubtext}>
                        {gameMode === 'single'
                            ? 'You conquered the castle!'
                            : `${winner === 'player1' ? 'Player 1' : 'Player 2'} reached the top first!`}
                    </p>
                </ImageBox>
                <div style={{ marginTop: '2vh' }}>
                    <ImageBox
                        src='answer-container.png'
                        style={styles.playAgainButton}
                        onClick={(e) => { this.startGame('multi'); }}
                        onTouchEnd={(e) => { e.preventDefault(); this.startGame('multi'); }}
                        imgScale={1.84}
                    >
                        <div style={styles.playAgainText}>Play Again</div>
                    </ImageBox>
                </div>
            </div>
        )
    }
    render() {
        const { phase } = this.state
        return (
            <div style={styles.gameContainer}>
                <img
                    src='/games/castle/castle-background.png'
                    alt='background'
                    style={styles.backgroundImage}
                />
                {phase === 'intro' && this.renderIntro()}
                {phase === 'playing' && this.renderPlaying()}
                {phase === 'victory' && this.renderVictory()}
            </div>
        )
    }
}
const styles = {
    gameContainer: {
        position: 'relative',
        width: '100vw',
        height: '100vh',
        overflow: 'hidden',
        fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
    },
    backgroundImage: {
        position: 'fixed',
        top: 0, left: 0, zIndex: 0,
        width: '100vw', height: '100vh',
        objectFit: 'cover',
    },
    overlay: {
        position: 'relative',
        zIndex: 1,
        width: '100%',
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        transform: 'translateY(4vh)'
    },
    // ── Intro ──
    introCard: {
        width: '42vh',
        height: '32vh',
        marginBottom: '8vh',
        padding: '2vh',
    },
    title: {
        fontSize: '4vh',
        fontWeight: 'bold',
        margin: '0 0 1.5vh 0',
        marginTop: '-4vh',
        color: '#222',
    },
    description: {
        fontSize: '1.8vh',
        lineHeight: 1.6,
        color: '#333',
        margin: 0,
        padding: '0 1vh',
    },
    modeButtons: {
        display: 'flex',
        gap: '10vh',
        justifyContent: 'center',
    },
    modeButton: {
        width: '24vh',
        height: '24vh',
        cursor: 'pointer',
        userSelect: 'none',
        WebkitTapHighlightColor: 'transparent',
        touchAction: 'manipulation',
    },
    modeIcon: {
        fontSize: '5vh',
        marginBottom: '0.8vh',
    },
    modeLabel: {
        fontSize: '2vh',
        fontWeight: '600',
        color: '#222',
    },
    // ── Scoreboard ──
    scoreboard: {
        position: 'absolute',
        top: '2.5vh',
        left: '50%',
        transform: 'translateX(-50%)',
        display: 'flex',
        gap: '65vh',
        zIndex: 10,
    },
    scoreItem: {
        width: '22vh',
        height: '20vh',
        padding: '1vh',
        transition: 'filter 0.3s ease',
    },
    scoreLabel: {
        fontSize: '2vh',
        fontWeight: '600',
        textTransform: 'uppercase',
        letterSpacing: '0.1vh',
        marginBottom: '0.2vh',
    },
    scoreValue: {
        fontSize: '3vh',
        fontWeight: 'bold',
        color: '#111',
    },
    scoreBar: {
        marginTop: '0.5vh',
        height: '1vh',
        width: '10vh',
        borderRadius: '0.3vh',
        backgroundColor: 'rgba(0,0,0,0.15)',
        //overflow: 'hidden',
    },
    scoreBarFill: {
        height: '100%',
        borderRadius: '0.3vh',
        transition: 'width 0.4s ease',
    },
    // ── Turn badge ──
    turnBadge: {
        position: 'absolute',
        top: '5.5vh',
        width: '20vh',
        height: '13vh',
        marginBottom: '1.5vh',
        textAlign: 'center',
        fontWeight: '700',
        fontSize: '2vh',
        textTransform: 'uppercase',
        letterSpacing: '0.1vh',
        color: '#222',
    },
    // ── Question area ──
    questionArea: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        //padding: '0 2vh',
    },
    questionCard: {
        width: '50vh',
        height: '24vh',
        //padding: '3vh',
        marginTop: '13vh',
        marginBottom: '10vh',
    },
    questionText: {
        fontSize: '2.8vh',
        fontWeight: '600',
        color: '#222',
        lineHeight: 1.5,
        textAlign: 'center',
        marginTop: '-4vh',
    },
    // ── Answers ──
    answersContainer: {
        display: 'flex',
        flexDirection: 'row',
        gap: '7vh',
        justifyContent: 'center',
        flexWrap: 'wrap',
    },
    answerCard: {
        width: '22vh',
        height: '22vh',
        //padding: '1.5vh',
        cursor: 'pointer',
        userSelect: 'none',
        WebkitTapHighlightColor: 'transparent',
        touchAction: 'manipulation',
        transition: 'transform 0.2s ease, filter 0.2s ease, opacity 0.2s ease',
    },
    answerText: {
        fontSize: '2vh',
        color: '#222',
        fontWeight: '600',
        lineHeight: 1.4,
        textAlign: 'center',
    },
    // ── Feedback states on answer cards ──
    correctAnswer: {
        transform: 'scale(1.06)',
    },
    incorrectAnswer: {
        transform: 'scale(0.94)',
    },
    fadedAnswer: {
        opacity: 0.3,
        transform: 'scale(0.94)',
    },
    feedbackIcon: {
        width: '3.5vh',
        height: '3.5vh',
        borderRadius: '50%',
        backgroundColor: '#2E7D32',
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '2vh',
        fontWeight: 'bold',
        marginBottom: '0.5vh',
    },
    feedbackIconWrong: {
        backgroundColor: '#C62828',
    },
    // ── Feedback banner ──
    feedbackBanner: {
        /* vertically and horizontally center */
        position: 'absolute',
        top: '55%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '24vh',
        height: '24vh',
        marginTop: '2vh',
        transition: 'filter 0.2s ease',
    },
    feedbackText: {
        fontSize: '2.8vh',
        fontWeight: 'bold',
        textAlign: 'center',
    },
    // ── Victory ──
    victoryCard: {
        width: '44vh',
        height: '30vh',
        padding: '3vh',
        paddingTop: '0vh',
        marginTop: '3vh',
    },
    victoryEmoji: {
        fontSize: '7vh',
        marginBottom: '1vh',
    },
    victoryTitle: {
        fontSize: '4vh',
        fontWeight: 'bold',
        margin: '0 0 1vh 0',
        color: '#222',
    },
    victorySubtext: {
        fontSize: '2vh',
        color: '#444',
        margin: '0',
    },
    victoryCharacter: {
        width: '24vh',
        height: '24vh',
        objectFit: 'contain',
        marginBottom: '7vh',
    },
    playAgainButton: {
        width: '24vh',
        height: '24vh',
        cursor: 'pointer',
        userSelect: 'none',
        WebkitTapHighlightColor: 'transparent',
        touchAction: 'manipulation',
    },
    playAgainText: {
        fontSize: '2.2vh',
        fontWeight: 'bold',
        color: '#222',
    },
}
export default CastleGame